import React, {
  FC,
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { IntlProvider } from "react-intl";

import dayjs from "dayjs";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import isBetweenPlugin from "dayjs/plugin/isBetween";
dayjs.extend(isBetweenPlugin);

// The 24 official languages of the European Union, ordered by native label.
export const supportedLanguagesWithLabel = new Map([
  ["cs", "Čeština"],
  ["da", "Dansk"],
  ["de", "Deutsch"],
  ["et", "Eesti"],
  ["en", "English"],
  ["es", "Español"],
  ["fr", "Français"],
  ["ga", "Gaeilge"],
  ["hr", "Hrvatski"],
  ["it", "Italiano"],
  ["lv", "Latviešu"],
  ["lt", "Lietuvių"],
  ["hu", "Magyar"],
  ["mt", "Malti"],
  ["nl", "Nederlands"],
  ["pl", "Polski"],
  ["pt", "Português"],
  ["ro", "Română"],
  ["sk", "Slovenčina"],
  ["sl", "Slovenščina"],
  ["fi", "Suomi"],
  ["sv", "Svenska"],
  ["el", "Ελληνικά"],
  ["bg", "Български"],
]);

export const supportedLanguages = Array.from(
  supportedLanguagesWithLabel.keys()
);

// English is the source language, its messages are the defaultMessage of each
// FormattedMessage, so there is nothing to load for it.
const loadMessages = async (lang: string) => {
  if (lang === "en") {
    return {};
  }
  const messages = await import(
    /* webpackChunkName: "lang-[request]" */ `./lang/${lang}.json`
  );
  return messages.default as Record<string, string>;
};

// dayjs ships an "en" locale by default, the others have to be registered.
const loadDayjsLocale = async (lang: string) => {
  if (lang === "en") {
    return;
  }
  await import(
    /* webpackChunkName: "dayjs-locale-[request]" */
    /* webpackInclude: /\/(bg|cs|da|de|el|es|et|fi|fr|ga|hr|hu|it|lt|lv|mt|nl|pl|pt|ro|sk|sl|sv)\.js$/ */
    `dayjs/locale/${lang}.js`
  );
};

const detectLanguage = () => {
  const savedLang = localStorage.getItem("lang");
  if (savedLang !== null && supportedLanguages.indexOf(savedLang) > -1) {
    return savedLang;
  }
  const preferred = navigator.languages ?? [navigator.language];
  for (const locale of preferred) {
    // navigator.language is a BCP 47 tag, we only support the base language
    const lang = locale.split("-")[0].toLowerCase();
    if (supportedLanguages.indexOf(lang) > -1) {
      return lang;
    }
  }
  return "en";
};

interface Props {
  children: React.ReactNode;
}

export const LangContext = createContext({
  language: "en",
  setLanguage: (locale: string) => {},
});

export const I18nProvider: FC<Props> = (props) => {
  // locale is only updated once the messages of that language are loaded, so
  // that we never render a half translated page.
  const [locale, setLocale] = useState("en");
  const [messages, setMessages] = useState<Record<string, string>>({});

  // the last language asked for: the messages of an earlier one that load
  // after it are dropped (e.g. the detected language and the lang= of a link,
  // see BillingCheckout.tsx)
  const requested = useRef<string | null>(null);
  const applyLanguage = useCallback(async (lang: string) => {
    requested.current = lang;
    const [loadedMessages] = await Promise.all([
      loadMessages(lang),
      loadDayjsLocale(lang),
    ]);
    if (requested.current !== lang) return;
    setMessages(loadedMessages);
    setLocale(lang);
  }, []);

  const setLanguage = useCallback(
    (lang: string) => {
      applyLanguage(lang);
      // If there is a /users/ iframe in the page, set the locale to reload, otherwise call set_locale to set the locale cookie
      // @ts-ignore
      const iframe: HTMLIFrameElement | null = document.querySelector("iframe");
      if (iframe && iframe.src.indexOf("/users/") > -1) {
        const src = iframe.src.split("?locale")[0];
        iframe.src = `${src}?locale=${lang}`;
      } else {
        fetch(`/users/set_locale?locale=${lang}`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({}),
        })
          .then((response) => {})
          .catch((error) => {});
      }
    },
    [applyLanguage]
  );

  useEffect(() => {
    applyLanguage(detectLanguage());
  }, [applyLanguage]);

  const contextValue = useMemo(
    () => ({
      language: locale,
      setLanguage,
    }),
    [locale, setLanguage]
  );

  useEffect(() => {
    localStorage.setItem("lang", locale);
    document.querySelector("html")?.setAttribute("lang", locale);
  }, [locale]);

  return (
    <IntlProvider messages={messages} locale={locale} defaultLocale="en">
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={locale}>
        <LangContext.Provider value={contextValue}>
          {props.children}
        </LangContext.Provider>
      </LocalizationProvider>
    </IntlProvider>
  );
};
